import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

const LocationSelector = ({ value, onChange, className }) => {
  const buildings = useSelector((state) => state.buildings || []);
  const yards = useSelector((state) => state.yards || []);
  const sessionUser = useSelector((state) => state.session.user);

  // Standard locations
  const standardLocations = ['Road', 'Shop'];

  // State for dropdown and search
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get all available locations
  const getAllLocations = () => {
    const locations = [...standardLocations];

    // Add buildings with door numbers
    buildings
      .filter(building => building.account_id === sessionUser.account_id)
      .forEach(building => {
        // Add doors for each building
        for (let i = 1; i <= building.building_door_number; i++) {
          locations.push(`${building.building_name}-${i}`);
        }
      });

    // Add yards
    yards
      .filter(yard => yard.account_id === sessionUser.account_id)
      .forEach(yard => {
        locations.push(yard.name);
      });

    return locations.sort((a, b) => {
      // Put standard locations first
      if (standardLocations.includes(a) && !standardLocations.includes(b)) return -1;
      if (!standardLocations.includes(a) && standardLocations.includes(b)) return 1;
      if (standardLocations.includes(a) && standardLocations.includes(b)) {
        return standardLocations.indexOf(a) - standardLocations.indexOf(b);
      }

      // Split locations into parts (e.g., "Building-1" becomes ["Building", "1"])
      const partsA = a.split('-');
      const partsB = b.split('-');

      // Compare building/yard names first
      if (partsA[0] !== partsB[0]) {
        return partsA[0].localeCompare(partsB[0]);
      }

      // If building names are the same, compare door numbers numerically
      if (partsA.length > 1 && partsB.length > 1) {
        return parseInt(partsA[1]) - parseInt(partsB[1]);
      }

      return a.localeCompare(b);
    });
  };

  // Apply search filter
  const filteredLocations = searchTerm
    ? getAllLocations().filter(location =>
        location.toLowerCase().includes(searchTerm.toLowerCase()))
    : getAllLocations();

  // Handle selection
  const handleSelect = (location) => {
    onChange(location);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle keyboard events for accessibility
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
      setIsFocused(true);
    } else if (e.key === 'Tab') {
      setIsFocused(false);
    } else if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        {/* Display selected value or placeholder */}
        <div
          className={`flex items-center w-full p-3 text-xl border rounded-md cursor-pointer border-gray-300 outline-none
            ${isFocused || isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}
            ${className || ''}`}
          onClick={() => setIsOpen(!isOpen)}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            if (!dropdownRef.current.contains(e.relatedTarget)) {
              setIsFocused(false);
            }
          }}
          tabIndex="0"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          onKeyDown={handleKeyDown}
        >
          {value ? (
            <span className="flex-grow truncate">{value}</span>
          ) : (
            <span className="flex-grow truncate text-gray-400">Select Location</span>
          )}
          <svg
            className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        {/* Search input and dropdown */}
        {isOpen && (
          <div className="absolute left-0 right-0 mt-0.5 top-full bg-white rounded-md shadow-lg z-50 border border-gray-200">
            {/* Search input */}
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search locations..."
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>

            {/* Locations list */}
            <div className="max-h-60 overflow-y-auto">
              {filteredLocations.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500">No matching locations</div>
              ) : (
                filteredLocations.map((location, idx) => (
                  <div
                    key={`location-${location}-${idx}`}
                    className={`px-4 py-2 cursor-pointer hover:bg-blue-100 focus:bg-blue-100
                      ${value === location ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500' : ''}`}
                    onClick={() => handleSelect(location)}
                    tabIndex="0"
                    role="option"
                    aria-selected={value === location}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect(location);
                      }
                    }}
                  >
                    {location}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationSelector;
